import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { PermSetActions } from '../../../../../../../modules/permission/perm-set-controller/actions';
import { PermSetSelectors } from '../../../../../../../modules/permission/perm-set-controller/selectors';
import { PermissionStates } from '../../../../../permission-controller/root';
import { gridOptionsClientSide } from '../../../../../../../listing.utils';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { SubSink } from 'subsink2';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-perm-set-add-lines',
  templateUrl: './perm-set-add-lines.component.html',
  styleUrls: ['./perm-set-add-lines.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermSetAddLinesComponent extends ViewColumnComponent {

  protected readonly index = 3;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  prevIndex: number;
  private prevLocalState: any;

  // initial ag grid state
  gridApi;
  gridOptions = gridOptionsClientSide;
  columnsDefs;
  rowSelection = 'single';
  rowData$ = [];

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;
  searchValue: any;

  //translation variable
  title: string;
  back_button: string;
  search_text: string;
  code_text: string;
  name_text: string;
  type_text: string;
  default_target_table_text: string;
  default_target_text: string;

  constructor(
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.configure_permission_set_lines;
        this.back_button = res.back_button;
        this.search_text = res.search;
        this.code_text = res.code;
        this.name_text = res.name;
        this.type_text = res.type;
        this.default_target_table_text = res.default_target_table;
        this.default_target_text = res.default_target;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Configure Permission Set Lines';
      this.back_button = 'Back';
      this.search_text = 'Search';
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.type_text = 'Type';
      this.default_target_table_text = 'Default Target Table';
      this.default_target_text = 'Default Target';
    }
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
  ngOnInit(): void {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.columnsDefs = [
      {
        headerName: this.code_text,
        field: 'app_perm_set_line.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'app_perm_set_line.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.type_text,
        field: 'app_perm_set_line.type',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.default_target_table_text,
        field: 'app_perm_set_line.target_hdr_table',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.default_target_text,
        field: 'default_target_hdr_name',
        cellStyle: {textAlign: 'left'},
      }
    ];
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.store.select(PermSetSelectors.selectNewPermSet)
    .subscribe(permSet => {
      this.rowData$ = [];

      permSet.permSetLines.forEach(line => {
        this.rowData$.push(line);
      });

      this.gridApi.setRowData(this.rowData$);
    });
  }

  /**
   * This function is called every time the user clicks on a row in the AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // save selected perm set line in store
    this.store.dispatch(PermSetActions.selectPermSetLine({permSetLine: entity}));

    // deactivate add, return and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true,
          deactivateAdd: true,
          deactivateList: true
        });
      // trigger the edit permission set line view column
      this.viewColFacade.onNext(4);
    }
  }

  /**
   * This functions is called when the user types something in the search bar.
   * Will cause the AG grid to filter its data accordingly.
   *
   */
   onSearch() {
    this.gridApi.setQuickFilter(this.searchValue);
  }

  /**
   * This function is called when the user clears the search bar. Will cause
   * the AG grid to show all the row data accordingly.
   *
   */
   reset() {
    this.searchValue = '';
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
